// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 07-source-control-pipeline.md
// Pipeline-run detail content — rendered inside the routed slide-over on
// /apps/:id/runs/:runId (docs/28 §Pipeline run detail). The route stays
// normative so alerts can deep-link a run; this component owns everything
// inside the panel: summary line, per-check rows, on-demand raw logs (the
// audited detail=full read), and the OPA decision.
import * as React from 'react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import {
  PipelineCheckRow,
  type PipelineCheckStatus,
} from '@/components/greenlight/PipelineCheckRow';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { RefreshWarning } from '@/components/greenlight/RefreshWarning';
import { AD_CARD, AD_CARD_HD } from '@/components/greenlight/surfaces';
import { pipelineMeta } from '@/components/greenlight/appStatusMeta';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { ACTIVE_POLL_MS, isTransientQueryError, isWorking, liveQueryOptions } from '@/lib/polling';

function checkRowStatus(s: string): PipelineCheckStatus {
  if (s === 'pass') return 'pass';
  if (s === 'fail') return 'fail';
  if (s === 'running') return 'running';
  return 'skipped';
}

export function PipelineRunDetail({
  appId,
  runId,
}: {
  appId: string;
  runId: string;
}): React.JSX.Element {
  // The `summary` read is the cheap verdict that drives the whole panel; it's the
  // only query whose loading/error state gates the view. Raw logs are the audited
  // `detail=full` read (proxies runner output live), so they load on demand as a
  // SEPARATE query — both keyed on runId so neither shows another run's data, and a
  // failed log read degrades to "no logs" instead of blanking a run we've loaded.
  const [showLogs, setShowLogs] = React.useState(false);
  // The logs opt-in is per-run: reset it the moment the run changes (render-phase,
  // before the full query's `enabled` is read) so navigating between runs never
  // auto-fires the audited detail=full read — each run needs a fresh "Load raw logs".
  const [logsOptInFor, setLogsOptInFor] = React.useState(runId);
  if (logsOptInFor !== runId) {
    setLogsOptInFor(runId);
    setShowLogs(false);
  }
  const summaryKey = ['pipeline.getRun', appId, runId, 'summary'];
  const summary = api.pipeline.getRun.useQuery(
    summaryKey,
    { params: { id: appId, runId }, query: { detail: 'summary' } },
    {
      ...liveQueryOptions,
      queryKey: summaryKey,
      refetchInterval: ({ state }) =>
        state.data && !isWorking(state.data.body.run.status) ? false : ACTIVE_POLL_MS,
    },
  );
  const fullKey = ['pipeline.getRun', appId, runId, 'full'];
  const full = api.pipeline.getRun.useQuery(
    fullKey,
    { params: { id: appId, runId }, query: { detail: 'full' } },
    { queryKey: fullKey, enabled: showLogs },
  );

  if (summary.isLoading) {
    return (
      <div className="space-y-3" data-testid="run-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (
    (summary.isError && !isTransientQueryError(summary.error)) ||
    !summary.data ||
    summary.data.status !== 200
  ) {
    return (
      <Alert variant="destructive" data-testid="run-error">
        <AlertTitle>Pipeline run not found</AlertTitle>
        <AlertDescription>The run may have been pruned.</AlertDescription>
      </Alert>
    );
  }
  // Raw logs are opt-in; the live summary owns the run and check verdicts.
  const fullBody = showLogs && full.data?.status === 200 ? full.data.body : null;
  const { run, checks, opa_decision } = summary.data.body;
  // Only a *settled* failure counts: the gated read threw, or returned a non-200.
  // Everything else without a body yet — the gap between the opt-in click and the
  // fetch starting, or a fetch in flight — is still "loading", so we never flash
  // "Couldn't load" / "No raw logs" before the read has actually finished.
  const logsFailed = showLogs && (full.isError || (full.data != null && full.data.status !== 200));
  const logsLoading = showLogs && !fullBody && !logsFailed;
  const meta = pipelineMeta(run.status);
  const duration =
    run.completedAt && run.triggeredAt
      ? Math.max(
          0,
          Math.round(
            (new Date(run.completedAt).getTime() - new Date(run.triggeredAt).getTime()) / 1000,
          ),
        )
      : null;

  return (
    <div className="space-y-5" data-testid="run-detail-page">
      {summary.isError && <RefreshWarning />}
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
        <MonoValue>{run.commitSha.slice(0, 7)}</MonoValue>
        <span>{run.branch}</span>
        <span>
          Triggered <span title={run.triggeredAt}>{relativeTime(run.triggeredAt)}</span>
        </span>
        {duration != null && <span>· {duration}s</span>}
      </div>

      <div data-testid="release-notes" className={cn(AD_CARD, 'overflow-hidden')}>
        <div className={AD_CARD_HD}>
          <h3 className="text-sm font-semibold">Release notes</h3>
        </div>
        <div className="p-5">
          {run.releaseNotes ? (
            // PR bodies are untrusted input: plain text only (React-escaped,
            // whitespace preserved) — never rendered as markdown/HTML.
            <p className="whitespace-pre-wrap break-words text-sm text-foreground">
              {run.releaseNotes}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No release notes for this run.</p>
          )}
        </div>
      </div>

      <div className={cn(AD_CARD, 'overflow-hidden')}>
        <div className={AD_CARD_HD}>
          <h3 className="text-sm font-semibold">Checks</h3>
          {checks.length > 0 &&
            (!showLogs ? (
              <Button variant="ghost" size="sm" onClick={() => setShowLogs(true)}>
                Load raw logs
              </Button>
            ) : logsLoading ? (
              <span className="text-xs text-muted-foreground">Loading logs…</span>
            ) : logsFailed ? (
              <span className="text-xs text-[hsl(var(--status-red-text))]">
                Couldn't load raw logs.
              </span>
            ) : fullBody?.checks.some((c) => c.logs) ? null : (
              <span className="text-xs text-muted-foreground">No raw logs for this run.</span>
            ))}
        </div>
        <div>
          {checks.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No checks recorded for this run.</p>
          ) : (
            checks.map((c) => (
              <PipelineCheckRow
                key={c.id}
                name={c.name}
                status={checkRowStatus(c.status)}
                detail={
                  c.error_summary ? (
                    <span className="text-[hsl(var(--status-red-text))]">{c.error_summary}</span>
                  ) : c.duration_ms != null ? (
                    `${c.duration_ms}ms`
                  ) : null
                }
                suggestedFix={c.suggested_fix}
                details={c.details}
                logs={fullBody?.checks.find((logged) => logged.id === c.id)?.logs}
              />
            ))
          )}
        </div>
      </div>

      <div data-testid="opa-decision" className={cn(AD_CARD, 'overflow-hidden')}>
        <div className={AD_CARD_HD}>
          <h3 className="text-sm font-semibold">OPA decision</h3>
        </div>
        <div className="p-5">
          {!opa_decision ? (
            <p className="text-sm text-muted-foreground">No OPA decision recorded.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <StatusBadge variant={opa_decision.allow ? 'granted' : 'failed'}>
                  {opa_decision.allow ? 'Allow' : 'Deny'}
                </StatusBadge>
                <MonoValue>{opa_decision.policy_id}</MonoValue>
                <span className="text-xs text-muted-foreground">
                  <span title={opa_decision.evaluated_at}>
                    {relativeTime(opa_decision.evaluated_at)}
                  </span>
                </span>
              </div>
              {opa_decision.reasons.length > 0 && (
                <ul className="list-disc pl-5 text-sm">
                  {opa_decision.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              )}
              <pre className="overflow-x-auto rounded-gl border border-border bg-muted/40 p-3 text-[11px] leading-5">
                {JSON.stringify(opa_decision.payload, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
